import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { systemKeys } from '../query-keys';
import { toast } from 'sonner';
import { toastApiError } from './toast-api-error';
import i18next from '@/lib/i18n';
import {
  systemLogoRequestPresignedUrlContract,
  systemLogoConfirmUploadContract,
  deleteSystemLogoContract,
  type SystemLogoRequestPresignedUrlRequest,
  type SystemLogoRequestPresignedUrlResponse,
  type SystemLogoConfirmUploadRequest,
  type SystemLogoConfirmUploadResponse,
  type DeleteSystemLogoRequest,
  type DeleteSystemLogoResponse
} from '@bliks-io/contracts';

/**
 * Request Presigned URL mutation for system logo
 *
 * Requests a presigned URL for uploading a system logo directly to DigitalOcean Spaces.
 * This is the first step in the system logo upload flow.
 *
 * @example
 * ```tsx
 * function SystemLogoUpload({ systemId }: { systemId: string }) {
 *   const requestUrl = useRequestSystemLogoUploadUrl();
 *
 *   const handleUpload = async (file: File) => {
 *     try {
 *       const presignedData = await requestUrl.mutateAsync({ systemId });
 *       // Use presignedData to upload file to S3
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={requestUrl.isPending}>Upload Logo</Button>;
 * }
 * ```
 */
export function useRequestSystemLogoUploadUrl() {
  return useMutation({
    mutationFn: async (
      request: SystemLogoRequestPresignedUrlRequest
    ): Promise<SystemLogoRequestPresignedUrlResponse> => {
      const { data } = await apiClient.post<SystemLogoRequestPresignedUrlResponse>(
        systemLogoRequestPresignedUrlContract.path,
        request
      );
      return data;
    },
    onError: (error) => {
      toastApiError(error, 'logo.prepareError');
    }
  });
}

/**
 * Confirm System Logo Upload mutation
 *
 * Confirms the system logo upload and triggers server-side processing.
 * This is the final step after uploading the file to the presigned URL.
 * The server will process the image/SVG appropriately.
 *
 * @example
 * ```tsx
 * function SystemLogoUpload({ systemId }: { systemId: string }) {
 *   const confirmUpload = useConfirmSystemLogoUpload();
 *
 *   const handleConfirm = async (key: string, mimeType: string) => {
 *     try {
 *       const result = await confirmUpload.mutateAsync({ systemId, key, mimeType });
 *       toast.success(i18next.t('logo.uploadSuccess'));
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={confirmUpload.isPending}>Confirm Upload</Button>;
 * }
 * ```
 */
export function useConfirmSystemLogoUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      request: SystemLogoConfirmUploadRequest
    ): Promise<SystemLogoConfirmUploadResponse> => {
      const { data } = await apiClient.post<SystemLogoConfirmUploadResponse>(
        systemLogoConfirmUploadContract.path,
        request
      );
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate system to refetch with new logo
      queryClient.invalidateQueries({ queryKey: systemKeys.detail(variables.systemId) });
      queryClient.invalidateQueries({ queryKey: systemKeys.lists() });

      toast.success(i18next.t('logo.uploadSuccess'));
    },
    onError: (error) => {
      toastApiError(error, 'logo.uploadError');
    }
  });
}

/**
 * Delete System Logo mutation
 *
 * Deletes the system's logo and all associated files from storage.
 *
 * @example
 * ```tsx
 * function SystemLogoUpload({ systemId }: { systemId: string }) {
 *   const deleteLogo = useDeleteSystemLogo();
 *
 *   const handleDelete = async () => {
 *     try {
 *       await deleteLogo.mutateAsync({ systemId });
 *       toast.success(i18next.t('logo.deleteSuccess'));
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return <Button loading={deleteLogo.isPending}>Delete Logo</Button>;
 * }
 * ```
 */
export function useDeleteSystemLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: DeleteSystemLogoRequest): Promise<DeleteSystemLogoResponse> => {
      const { data } = await apiClient.delete<DeleteSystemLogoResponse>(
        deleteSystemLogoContract.path.replace(':systemId', request.systemId)
      );
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate system to refetch without logo
      queryClient.invalidateQueries({ queryKey: systemKeys.detail(variables.systemId) });
      queryClient.invalidateQueries({ queryKey: systemKeys.lists() });

      toast.success(i18next.t('logo.deleteSuccess'));
    },
    onError: (error) => {
      toastApiError(error, 'logo.deleteError');
    }
  });
}
